import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuth } from './auth/AuthContext.js';
import {
  modules,
  phase3NavEnabled,
  type ModuleChild,
  type ModuleEntry,
} from './modules/registry.js';
import TokenUsageBadge from './components/TokenUsageBadge.js';
import UserMenu from './components/UserMenu.js';
import { cn } from './lib/cn.js';

interface AppShellProps {
  /** When provided, render this content in the workspace area instead of <Outlet/>. */
  children?: ReactNode;
  /** Minimal layout (no sidebar) — used for login. */
  minimal?: boolean;
}

function isChildActive(pathname: string, child: ModuleChild): boolean {
  if (child.exact) return pathname === child.to;
  if (child.to === '/') return pathname === '/';
  return pathname === child.to || pathname.startsWith(child.to + '/');
}

function isModuleActive(pathname: string, mod: ModuleEntry): boolean {
  if (mod.to) {
    if (mod.to === '/') return pathname === '/';
    return pathname === mod.to || pathname.startsWith(mod.to + '/');
  }
  return (mod.children ?? []).some((c) => isChildActive(pathname, c));
}

function Sidebar({ isAdmin }: { isAdmin: boolean }) {
  const { pathname } = useLocation();
  const phase3Nav = phase3NavEnabled();
  const visible = modules.filter((m) => !m.adminOnly || isAdmin);

  return (
    <aside
      className="hidden h-full w-60 shrink-0 overflow-hidden border-r border-slate-200 bg-white md:flex md:flex-col"
      data-testid="app-sidebar"
    >
      <div className="px-4 py-4">
        <Link
          to="/"
          className="flex items-baseline gap-1.5 text-base font-semibold tracking-tight text-slate-900"
        >
          <span className="text-xs font-normal text-slate-500">链上科技</span>
          <span>Sitebox</span>
        </Link>
      </div>
      <nav className="min-h-0 flex-1 space-y-4 overflow-y-auto px-2 pb-6" aria-label="primary">
        {visible.map((mod) => {
          const Icon = mod.icon;
          const moduleActive = isModuleActive(pathname, mod);
          if (mod.to && !mod.children) {
            return (
              <NavLink
                key={mod.id}
                to={mod.to}
                end={mod.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                  )
                }
                data-testid={`sidebar-module-${mod.id}`}
              >
                <Icon className="h-4 w-4" />
                <span>{mod.label}</span>
              </NavLink>
            );
          }
          return (
            <div key={mod.id} data-testid={`sidebar-module-${mod.id}`}>
              <div className="mb-1 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                <Icon className="h-3.5 w-3.5" />
                <span>{mod.label}</span>
              </div>
              <ul className="space-y-0.5">
                {(mod.children ?? [])
                  .filter(
                    (child) =>
                      !child.hidden &&
                      (!child.adminOnly || isAdmin) &&
                      (!child.phase3 || phase3Nav),
                  )
                  .map((child) => (
                    <li key={child.id}>
                      <NavLink
                        to={child.to}
                        end={child.exact}
                        className={() =>
                          cn(
                            'flex items-center rounded-md px-3 py-1.5 text-sm transition-colors',
                            isChildActive(pathname, child)
                              ? 'bg-slate-100 font-medium text-slate-900'
                              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                          )
                        }
                        data-testid={`sidebar-link-${child.id}`}
                        data-active={isChildActive(pathname, child) ? 'true' : 'false'}
                      >
                        {child.label}
                      </NavLink>
                    </li>
                  ))}
              </ul>
              {moduleActive ? null : null /* placeholder to silence unused-var */}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

function Topbar({ onToggleMobile }: { onToggleMobile: () => void }) {
  return (
    <header
      className="z-20 flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 md:px-6"
      data-testid="app-topbar"
    >
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onToggleMobile}
          className="shrink-0 rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 md:hidden"
          aria-label="toggle navigation"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link
          to="/"
          className="flex shrink-0 items-baseline gap-1.5 whitespace-nowrap text-base font-semibold tracking-tight text-slate-900 md:hidden"
        >
          <span className="hidden text-xs font-normal text-slate-500 sm:inline">链上科技</span>
          <span>Sitebox</span>
        </Link>
      </div>
      <div className="flex shrink-0 items-center gap-2 whitespace-nowrap text-sm md:gap-4">
        <TokenUsageBadge />
        <UserMenu />
      </div>
    </header>
  );
}

function MobileSidebar({
  open,
  onClose,
  isAdmin,
}: {
  open: boolean;
  onClose: () => void;
  isAdmin: boolean;
}) {
  if (!open) return null;
  const phase3Nav = phase3NavEnabled();
  const visible = modules.filter((m) => !m.adminOnly || isAdmin);
  return (
    <div className="fixed inset-0 z-30 md:hidden">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden />
      <aside className="relative flex h-full w-64 flex-col overflow-hidden bg-white shadow-xl">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 px-4">
          <span className="text-base font-semibold">Sitebox</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
          >
            ✕
          </button>
        </div>
        <nav className="min-h-0 flex-1 space-y-3 overflow-y-auto px-2 py-4">
          {visible.map((mod) => (
            <div key={mod.id}>
              {mod.to && !mod.children ? (
                <NavLink
                  to={mod.to}
                  end={mod.to === '/'}
                  onClick={onClose}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                >
                  <mod.icon className="h-4 w-4" />
                  {mod.label}
                </NavLink>
              ) : (
                <>
                  <div className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {mod.label}
                  </div>
                  <ul>
                    {(mod.children ?? [])
                      .filter(
                        (c) => !c.hidden && (!c.adminOnly || isAdmin) && (!c.phase3 || phase3Nav),
                      )
                      .map((c) => (
                        <li key={c.id}>
                          <NavLink
                            to={c.to}
                            end={c.exact}
                            onClick={onClose}
                            className={({ isActive }) =>
                              cn(
                                'block rounded-md px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100',
                                isActive && 'bg-slate-100 font-medium text-slate-900',
                              )
                            }
                          >
                            {c.label}
                          </NavLink>
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </div>
          ))}
        </nav>
      </aside>
    </div>
  );
}

export default function AppShell({ children, minimal = false }: AppShellProps) {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = Boolean(user?.is_admin);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    // 工作区自己滚了，换页就不再有浏览器帮忙回顶，得自己归位
    scrollRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  if (minimal) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <header className="border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
            <Link
              to="/"
              className="flex items-baseline gap-1.5 text-lg font-semibold tracking-tight text-slate-900"
            >
              <span className="text-xs font-normal text-slate-500">链上科技</span>
              <span>Sitebox</span>
            </Link>
          </div>
        </header>
        <main className="mx-auto max-w-6xl px-6 py-8">{children ?? <Outlet />}</main>
      </div>
    );
  }

  return (
    /*
     * 左右两个滚动区各管各的。
     *
     * 之前这里是 `min-h-screen`：侧边栏的 `overflow-y-auto` 永远不生效（父元素没高度
     * 上限，aside 被内容擑开），滚侧边栏实际滚的是整个文档 —— 右边正文跟着一起走。
     * 改成视口固定高度 + 外层不滚，滚动只发生在两个内层容器里：
     * 侧边栏的 <nav> 和右边的工作区。
     */
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-900">
      <Sidebar isAdmin={isAdmin} />
      <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} isAdmin={isAdmin} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar onToggleMobile={() => setMobileOpen((v) => !v)} />
        {/* 右边自己的滚动容器：页脚跟着正文走，不钉在屏底 */}
        <div ref={scrollRef} className="flex-1 overflow-auto" data-testid="app-scroll">
          {/*
           * 里面这层套一个 min-h-full 的 flex 列：页数少的时候正文撑开、页脚被压到底，
           * 页数多的时候整层跟着长、页脚跟在内容后面一起滚。
           * 少了这一层，短页面的页脚就悬在半空（内容有多高它就贴多高），很难看。
           */}
          <div className="flex min-h-full flex-col">
            <main className="flex-1 px-4 py-6 md:px-8 md:py-8" data-testid="app-workspace">
              <div className="mx-auto max-w-7xl">{children ?? <Outlet />}</div>
            </main>
            <footer className="shrink-0 border-t border-slate-100 px-4 py-4 text-xs text-slate-400 md:px-8">
              {`链上科技 Sitebox  v${__APP_VERSION__} Built ${__BUILD_TIME__}`}
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
